import React, { useEffect, useRef, useState } from 'react';
import { Menu, X, Globe, Phone } from 'lucide-react';
import { brand } from '../../data/mock';

const navItems = [
  { label: 'The Hotel', href: '#introduction' },
  { label: 'Rooms & Suites', href: '#stay' },
  { label: 'Dining', href: '#dining' },
  { label: 'Spa & Wellness', href: '#wellness' },
  { label: 'Experiences', href: '#experiences' },
  { label: 'Weddings', href: '#celebrations' },
  { label: 'Meetings', href: '#meetings' },
  { label: 'Istanbul', href: '#explore' },
];

const Header = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState('EN');
  const scrolledRef = useRef(false);
  const frameRef = useRef(0);

  useEffect(() => {
    const updateScrolled = () => {
      frameRef.current = 0;
      const nextScrolled = window.scrollY > 40;
      if (scrolledRef.current !== nextScrolled) {
        scrolledRef.current = nextScrolled;
        setScrolled(nextScrolled);
      }
    };
    const onScroll = () => {
      if (frameRef.current) return;
      frameRef.current = window.requestAnimationFrame(updateScrolled);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    updateScrolled();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
          scrolled || open ? 'bg-[#0f1216]/95 backdrop-blur-md border-b border-white/10' : 'bg-gradient-to-b from-black/40 to-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-[76px] md:h-[88px] flex items-center justify-between">
          {/* Left: menu */}
          <div className="flex items-center gap-6">
            <button
              onClick={() => setOpen(true)}
              className="flex items-center gap-3 text-white/90 hover:text-white transition-colors"
              aria-label="Open menu"
            >
              <span className="flex flex-col gap-[5px]">
                <span className="block w-6 h-px bg-current" />
                <span className="block w-6 h-px bg-current" />
                <span className="block w-4 h-px bg-current" />
              </span>
              <span className="hidden md:inline tracking-[0.22em] text-[11px] uppercase">Menu</span>
            </button>
            <a href="#dining" className="hidden lg:inline tracking-[0.22em] text-[11px] uppercase text-white/80 hover:text-white transition-colors">Dining</a>
            <a href="#stay" className="hidden lg:inline tracking-[0.22em] text-[11px] uppercase text-white/80 hover:text-white transition-colors">Stay</a>
          </div>

          {/* Center: logo */}
          <a href="#top" className="absolute left-1/2 -translate-x-1/2 text-center text-white select-none">
            <div className="font-serif tracking-[0.42em] text-[15px] md:text-[19px] leading-none">{brand.name}</div>
            <div className="mt-1 tracking-[0.32em] text-[8.5px] md:text-[9.5px] text-white/70">{brand.location}</div>
          </a>

          {/* Right */}
          <div className="flex items-center gap-4 md:gap-6">
            <button
              onClick={() => setLang(lang === 'EN' ? 'TR' : 'EN')}
              className="hidden md:flex items-center gap-2 text-white/85 hover:text-white transition-colors tracking-[0.2em] text-[11px] uppercase"
              aria-label="Change language"
            >
              <Globe className="w-4 h-4" strokeWidth={1.25} />
              {lang}
            </button>
            <a href={`tel:${brand.phone.replace(/\s/g, '')}`} className="hidden md:flex items-center gap-2 text-white/85 hover:text-white transition-colors tracking-[0.2em] text-[11px] uppercase">
              <Phone className="w-4 h-4" strokeWidth={1.25} />
              Contact
            </a>
            <button
              onClick={onBook}
              className="px-4 md:px-6 h-9 md:h-10 border border-white/70 text-white tracking-[0.22em] text-[10.5px] md:text-[11px] uppercase hover:bg-white hover:text-neutral-900 transition-colors"
            >
              Book
            </button>
          </div>
        </div>
      </header>

      {/* Fullscreen menu */}
      <div
        className={`fixed inset-0 z-50 bg-[#0b0e12] text-white transition-all duration-500 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-[76px] md:h-[88px] flex items-center justify-between">
          <span className="tracking-[0.22em] text-[11px] uppercase text-white/60">Menu</span>
          <a href="#top" className="text-center">
            <div className="font-serif tracking-[0.42em] text-[15px] md:text-[19px]">{brand.name}</div>
            <div className="mt-1 tracking-[0.32em] text-[8.5px] md:text-[9.5px] text-white/60">{brand.location}</div>
          </a>
          <button onClick={() => setOpen(false)} aria-label="Close" className="text-white/85 hover:text-white">
            <X className="w-6 h-6" strokeWidth={1.25} />
          </button>
        </div>
        <nav className="max-w-[1440px] mx-auto px-5 md:px-10 pt-8 md:pt-16 pb-16 grid md:grid-cols-2 gap-y-2 md:gap-y-4">
          {navItems.map((item, i) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => setOpen(false)}
              className="group block py-4 md:py-5 border-b border-white/10"
              style={{ transitionDelay: `${i * 40}ms` }}
            >
              <span className="font-serif text-3xl md:text-5xl tracking-tight group-hover:text-white/70 transition-colors">
                {item.label}
              </span>
            </a>
          ))}
        </nav>
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pb-10 flex flex-wrap gap-6 text-[11px] tracking-[0.22em] uppercase text-white/60">
          <span>{brand.address}</span>
          <span>·</span>
          <a href={`tel:${brand.phone.replace(/\s/g, '')}`} className="hover:text-white">{brand.phone}</a>
          <span>·</span>
          <a href={`mailto:${brand.email}`} className="hover:text-white">{brand.email}</a>
        </div>
      </div>
    </>
  );
};

export default Header;
